import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragOverlay
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Loading from '../Loading';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EmptyState from '../Common/EmptyState';
import {
  getProjectEntries,
  hasNoCustomProjects,
  isDefaultProject
} from '../../utils/managementNotifications';

const REPO_PLACEMENT_SEPARATOR = '::repo::';
const PROJECT_SUGGESTIONS_URL = '/project-management/suggestions';

const getRepoPlacementId = (projectUid, repoId) =>
  `${projectUid}${REPO_PLACEMENT_SEPARATOR}${repoId}`;

const parseRepoPlacementId = placementId => {
  if (typeof placementId !== 'string') return null;

  const separatorIndex = placementId.indexOf(REPO_PLACEMENT_SEPARATOR);
  if (separatorIndex === -1) return null;

  return {
    projectUid: placementId.slice(0, separatorIndex),
    repoId: placementId.slice(separatorIndex + REPO_PLACEMENT_SEPARATOR.length)
  };
};

const buildRepoProjectPayload = ({
  repoId,
  fromProjectUid,
  toProjectUid,
  operation
}) => ({
  repoId,
  projectUid: toProjectUid,
  sourceProjectUid: fromProjectUid,
  operation
});

const getSuggestionSummary = suggestion => {
  const projectCount = suggestion?.groups?.length || 0;

  return `${projectCount} project creation suggestion${
    projectCount === 1 ? '' : 's'
  } pending`;
};

const buildProjectSuggestionDraftGroups = suggestion =>
  (suggestion?.groups || []).map((group, index) => ({
    id: `${group.project_name || 'project'}-${index}`,
    originalProjectName: group.project_name || '',
    projectName: group.project_name || '',
    status: 'approved',
    repos: (group.repos || []).map((repo, repoIndex) => ({
      ...repo,
      originalIndex: repoIndex
    })),
    removedRepos: []
  }));

const getProjectSuggestionDraftStats = groups => {
  const approvedGroups = groups.filter(
    group => group.status === 'approved' && group.repos.length > 0
  );
  const rejectedGroups = groups.filter(group => group.status === 'rejected');
  const removedRepoCount = groups.reduce(
    (total, group) => total + group.removedRepos.length,
    0
  );
  const approvedRepoCount = approvedGroups.reduce(
    (total, group) => total + group.repos.length,
    0
  );
  const invalidApprovedProjectNameCount = approvedGroups.filter(group => {
    const projectName = String(group.projectName || '')
      .trim()
      .toLowerCase();
    return !projectName || projectName === 'default';
  }).length;

  return {
    approvedGroups,
    approvedProjectCount: approvedGroups.length,
    approvedRepoCount,
    rejectedProjectCount: rejectedGroups.length,
    removedRepoCount,
    invalidApprovedProjectNameCount
  };
};

const applyRepoProjectAction = (
  prevProjects,
  { repoId, fromProjectUid, toProjectUid, operation }
) => {
  const sourceProject = prevProjects[fromProjectUid];
  const targetProject = prevProjects[toProjectUid];

  if (!sourceProject || !targetProject) {
    return prevProjects;
  }

  const repoToTransfer = sourceProject.repos.find(repo => repo.id === repoId);
  if (!repoToTransfer) {
    return prevProjects;
  }

  const targetHasRepo = targetProject.repos.some(repo => repo.id === repoId);
  const nextProjects = { ...prevProjects };

  if (operation === 'move') {
    nextProjects[fromProjectUid] = {
      ...sourceProject,
      repos: sourceProject.repos.filter(repo => repo.id !== repoId)
    };
  }

  if (!targetHasRepo) {
    nextProjects[toProjectUid] = {
      ...targetProject,
      repos: [...targetProject.repos, repoToTransfer]
    };
  }

  return nextProjects;
};

const RepoItem = ({
  id,
  repoId,
  name,
  dragOverlay,
  onRename,
  isSelected,
  onToggleSelect,
  isDarkMode
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(name);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging && !dragOverlay ? 0.5 : 1,
    boxShadow: dragOverlay ? '0 0 0 2px #3b82f6' : undefined,
    backgroundColor: dragOverlay
      ? isDarkMode
        ? '#313D4A'
        : '#f9fafb'
      : undefined
  };

  const handleRename = () => {
    if (newName.trim() && newName !== name) {
      onRename(repoId, newName.trim());
    } else {
      setNewName(name);
    }
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      className={`group mb-2 flex min-w-0 items-center justify-between rounded-xl border border-dashed p-2 shadow-sm transition ${
        isDarkMode
          ? isSelected
            ? 'border-blue-500 bg-blue-500/10 hover:border-blue-400 hover:bg-blue-500/15'
            : 'border-strokedark bg-boxdark hover:border-slate-500 hover:bg-meta-4'
          : isSelected
            ? 'border-blue-400 bg-blue-50/70 hover:border-slate-400 hover:bg-slate-50'
            : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50'
      }`}
      style={style}
    >
      {isEditing ? (
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onBlur={handleRename}
          onKeyDown={e => e.key === 'Enter' && handleRename()}
          autoFocus
          className={`w-full rounded-lg border px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isDarkMode
              ? 'border-strokedark bg-meta-4 text-white'
              : 'border-slate-200 bg-white'
          }`}
        />
      ) : (
        <>
          <label className="mr-2 flex flex-shrink-0 items-center">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(id)}
              className={`h-4 w-4 rounded text-blue-600 focus:ring-blue-500 ${
                isDarkMode
                  ? 'border-strokedark bg-transparent text-blue-400'
                  : 'border-slate-300'
              }`}
            />
          </label>
          <div
            {...attributes}
            {...listeners}
            onDoubleClick={() => setIsEditing(true)}
            className={`flex w-full min-w-0 cursor-grab items-center gap-2 active:cursor-grabbing ${
              isDarkMode ? 'text-slate-200' : 'text-slate-700'
            }`}
            title={name}
          >
            <DragIndicatorIcon
              className={`flex-shrink-0 ${
                isDarkMode ? 'text-slate-500' : 'text-slate-400'
              }`}
            />
            <span className="truncate">{name}</span>
          </div>
        </>
      )}
    </div>
  );
};

const ProjectColumn = ({
  projectUid,
  title,
  repos,
  onDelete,
  onRename,
  onRenameRepo,
  selectedRepoIds,
  onToggleRepoSelection,
  onToggleSelectAll,
  isDarkMode
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: projectUid });
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(title);
  const [repoSearchTerm, setRepoSearchTerm] = useState('');
  const normalizedRepoSearchTerm = repoSearchTerm.trim().toLowerCase();
  const filteredRepos = repos.filter(
    repo =>
      !normalizedRepoSearchTerm ||
      repo?.name?.toLowerCase().includes(normalizedRepoSearchTerm)
  );
  const selectedCount = repos.filter(repo =>
    selectedRepoIds.includes(getRepoPlacementId(projectUid, repo.id))
  ).length;
  const allReposSelected = repos.length > 0 && selectedCount === repos.length;

  const handleRename = () => {
    if (newTitle.trim() && newTitle !== title) {
      onRename(projectUid, newTitle.trim());
    }
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete(projectUid);
  };

  return (
    <div
      ref={setNodeRef}
      className={`relative flex h-full w-[290px] flex-col rounded-2xl border p-4 shadow-[0_14px_40px_rgba(15,23,42,0.08)] transition-all duration-200 ${
        isOver
          ? isDarkMode
            ? 'border-blue-500 bg-blue-500/10 ring-2 ring-blue-900/40'
            : 'border-blue-400 bg-blue-50/70 ring-2 ring-blue-100'
          : isDarkMode
            ? 'border-strokedark bg-boxdark'
            : 'border-slate-200 bg-white'
      }`}
    >
      <div
        className={`mb-4 flex items-center justify-between border-b pb-3 ${
          isDarkMode ? 'border-strokedark' : 'border-slate-200'
        }`}
      >
        {isEditing ? (
          <div className="flex w-full items-center gap-2">
            <input
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onBlur={handleRename}
              onKeyDown={e => e.key === 'Enter' && handleRename()}
              autoFocus
              className={`w-full rounded-lg border px-2 py-1 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode
                  ? 'border-strokedark bg-meta-4 text-white'
                  : 'border-slate-200 bg-white'
              }`}
            />
            <button
              onClick={handleRename}
              className="rounded-md bg-blue-600 px-2 py-1 text-white transition-colors hover:bg-blue-700"
            >
              Update
            </button>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between gap-3">
            <h4
              onClick={() => setIsEditing(true)}
              className={`flex cursor-pointer items-center gap-3 select-none text-lg font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              <span className="truncate">{title}</span>
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold shadow-sm ${
                  isDarkMode
                    ? 'bg-blue-500/15 text-blue-300'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {repos.length}
              </span>
            </h4>

            <button
              onClick={() => setIsEditing(true)}
              className={`rounded-md p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                isDarkMode ? 'hover:bg-meta-4' : 'hover:bg-slate-100'
              }`}
              aria-label="Edit title"
            >
              <EditIcon className="text-blue-600" fontSize="small" />
            </button>
          </div>
        )}
        {repos.length === 0 && (
          <button
            onClick={handleDelete}
            className="text-xs text-red-500 hover:text-red-700"
          >
            <DeleteIcon
              className="text-gray-600 hover:text-blue-600"
              fontSize="small"
            />
          </button>
        )}
      </div>

      {repos.length > 0 && (
        <div className="mb-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => onToggleSelectAll(projectUid)}
              className="text-xs font-medium text-blue-600 transition hover:text-blue-700"
            >
              {allReposSelected ? 'Clear column' : 'Select column'}
            </button>
            {selectedCount > 0 && (
              <span
                className={`text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                {selectedCount} selected
              </span>
            )}
          </div>
          <label className="relative block w-full">
            <SearchIcon
              className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-[18px] ${
                isDarkMode ? 'text-slate-500' : 'text-slate-400'
              }`}
            />
            <input
              type="text"
              value={repoSearchTerm}
              placeholder="Search repositories..."
              onChange={e => setRepoSearchTerm(e.target.value)}
              className={`h-9 w-full rounded-lg border pl-8 pr-3 text-xs shadow-sm outline-none transition-all placeholder:text-slate-400 focus:border-blue-400 focus:ring-2 ${
                isDarkMode
                  ? 'border-strokedark bg-meta-4 text-slate-100 placeholder:text-slate-500 focus:bg-boxdark focus:ring-blue-900/40'
                  : 'border-slate-200 bg-slate-50 text-slate-700 focus:bg-white focus:ring-blue-100'
              }`}
            />
          </label>
        </div>
      )}

      <div className="max-h-[420px] flex-1 overflow-y-auto pr-1">
        <SortableContext
          items={filteredRepos.map(repo =>
            getRepoPlacementId(projectUid, repo.id)
          )}
          strategy={rectSortingStrategy}
        >
          {repos.length === 0 ? (
            <EmptyState
              compact
              className={`min-h-[140px] border-dashed px-4 py-8 ${
                isDarkMode ? 'bg-meta-4' : 'bg-slate-50'
              }`}
              title="No Data Available"
              description="No repositories have been added to this project yet."
            />
          ) : filteredRepos.length === 0 ? (
            <div
              className={`rounded-lg border border-dashed px-3 py-8 text-center text-xs ${
                isDarkMode
                  ? 'border-strokedark bg-meta-4 text-slate-400'
                  : 'border-slate-300 bg-slate-50 text-slate-500'
              }`}
            >
              No matching repositories found.
            </div>
          ) : (
            filteredRepos.map(({ id, name }) => (
              <RepoItem
                key={getRepoPlacementId(projectUid, id)}
                id={getRepoPlacementId(projectUid, id)}
                repoId={id}
                name={name}
                isSelected={selectedRepoIds.includes(
                  getRepoPlacementId(projectUid, id)
                )}
                onToggleSelect={onToggleRepoSelection}
                isDarkMode={isDarkMode}
                onRename={(oldId, newName) =>
                  onRenameRepo(projectUid, oldId, newName)
                }
              />
            ))
          )}
        </SortableContext>
      </div>
    </div>
  );
};

const ProjectManagement = () => {
  const [projects, setProjects] = useState({});
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectSuggestion, setProjectSuggestion] = useState(null);
  const [projectSuggestionDraftGroups, setProjectSuggestionDraftGroups] =
    useState([]);
  const [isLoadingProjectSuggestion, setIsLoadingProjectSuggestion] =
    useState(true);
  const [isProjectSuggestionOpen, setIsProjectSuggestionOpen] = useState(false);
  const [projectSuggestionAction, setProjectSuggestionAction] = useState(null);
  const [projectSuggestionGroupAction, setProjectSuggestionGroupAction] =
    useState(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [selectedRepoIds, setSelectedRepoIds] = useState([]);
  const [bulkTargetProjectUid, setBulkTargetProjectUid] = useState('');
  const [bulkActionInFlight, setBulkActionInFlight] = useState(null);
  const [pendingRepoAction, setPendingRepoAction] = useState(null);
  const [isRepoActionSubmitting, setIsRepoActionSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );
  const boardScrollRef = useRef(null);
  const columnRefs = useRef({});
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const fetchProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const response = await sendRequest('/project-management/list', 'get');
      setProjects(response?.projects || {});
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects({});
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchProjectSuggestion = async () => {
    try {
      setIsLoadingProjectSuggestion(true);
      const response = await sendRequest(PROJECT_SUGGESTIONS_URL, 'GET');
      const suggestion = response?.data || null;
      setProjectSuggestion(suggestion);
      setProjectSuggestionDraftGroups(
        buildProjectSuggestionDraftGroups(suggestion)
      );
      setIsProjectSuggestionOpen(false);
    } catch (error) {
      console.error('Error fetching project suggestions:', error);
      setProjectSuggestion(null);
      setProjectSuggestionDraftGroups([]);
    } finally {
      setIsLoadingProjectSuggestion(false);
    }
  };

  const refreshProjectManagement = async () => {
    await Promise.all([fetchProjects(), fetchProjectSuggestion()]);
  };

  useEffect(() => {
    refreshProjectManagement();
  }, []);

  useEffect(() => {
    const container = boardScrollRef.current;
    if (!container) return undefined;

    const updateScrollState = () => {
      const { scrollLeft, clientWidth, scrollWidth } = container;
      setCanScrollLeft(scrollLeft > 8);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 8);
    };

    updateScrollState();
    container.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [projects]);

  useEffect(() => {
    if (!activeId) return undefined;

    const handlePointerMove = event => {
      const container = boardScrollRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const threshold = 140;
      const maxStep = 28;

      if (event.clientX >= rect.right - threshold) {
        const ratio = Math.min(
          1,
          (event.clientX - (rect.right - threshold)) / threshold
        );
        container.scrollBy({ left: Math.ceil(maxStep * (0.35 + ratio)) });
      } else if (event.clientX <= rect.left + threshold) {
        const ratio = Math.min(
          1,
          (rect.left + threshold - event.clientX) / threshold
        );
        container.scrollBy({ left: -Math.ceil(maxStep * (0.35 + ratio)) });
      }
    };

    window.addEventListener('pointermove', handlePointerMove, {
      passive: true
    });

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
    };
  }, [activeId]);

  useEffect(() => {
    if (!pendingRepoAction) return undefined;

    const handleEscape = event => {
      if (event.key === 'Escape' && !isRepoActionSubmitting) {
        setPendingRepoAction(null);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [pendingRepoAction, isRepoActionSubmitting]);

  const findContainer = id => {
    const parsedPlacement = parseRepoPlacementId(id);
    if (parsedPlacement?.projectUid && projects[parsedPlacement.projectUid]) {
      return parsedPlacement.projectUid;
    }

    return Object.keys(projects).find(key =>
      projects[key].repos.some(repo => repo.id === id)
    );
  };

  const orderedProjects = getProjectEntries(projects);
  const defaultProject = orderedProjects.find(project =>
    isDefaultProject(project)
  );
  const hasCustomProjects = !hasNoCustomProjects(projects);
  const selectedRepos = useMemo(
    () =>
      orderedProjects.flatMap(project =>
        project.repos
          .filter(repo =>
            selectedRepoIds.includes(getRepoPlacementId(project.uid, repo.id))
          )
          .map(repo => ({
            ...repo,
            projectUid: project.uid,
            projectName: project.name,
            placementId: getRepoPlacementId(project.uid, repo.id)
          }))
      ),
    [orderedProjects, selectedRepoIds]
  );

  const activeDraggedRepos = useMemo(() => {
    if (!activeId) return [];

    const activePlacement = parseRepoPlacementId(activeId);
    const fromContainer =
      activePlacement?.projectUid || findContainer(activeId);
    const repoId = activePlacement?.repoId || activeId;
    const activeRepo = fromContainer
      ? projects[fromContainer]?.repos.find(repo => repo.id === repoId)
      : null;

    if (!activeRepo || !fromContainer) {
      return [];
    }

    if (selectedRepoIds.includes(activeId) && selectedRepos.length > 1) {
      return selectedRepos;
    }

    return [
      {
        ...activeRepo,
        projectUid: fromContainer,
        projectName: projects[fromContainer]?.name,
        placementId: activeId
      }
    ];
  }, [activeId, projects, selectedRepoIds, selectedRepos]);

  const getReposForProjectAction = (repos, toProjectUid, operation) => {
    const targetRepoIds = new Set(
      (projects[toProjectUid]?.repos || []).map(repo => repo.id)
    );
    const seenRepoIdsToCopy = new Set();

    return repos.filter(repo => {
      if (repo.projectUid === toProjectUid) {
        return false;
      }

      if (operation === 'move') {
        return true;
      }

      if (targetRepoIds.has(repo.id) || seenRepoIdsToCopy.has(repo.id)) {
        return false;
      }

      seenRepoIdsToCopy.add(repo.id);
      return true;
    });
  };

  const scrollToProject = projectUid => {
    const container = boardScrollRef.current;
    const column = columnRefs.current[projectUid];

    if (!container || !column) return;

    const left =
      column.offsetLeft - container.clientWidth / 2 + column.clientWidth / 2;

    container.scrollTo({
      left: Math.max(0, left),
      behavior: 'smooth'
    });
  };

  const scrollToAdjacentProject = direction => {
    const container = boardScrollRef.current;
    if (!container || orderedProjects.length === 0) return;

    const visibleCenter = container.scrollLeft + container.clientWidth / 2;
    const centers = orderedProjects
      .map(project => {
        const column = columnRefs.current[project.uid];
        if (!column) return null;

        return {
          uid: project.uid,
          center: column.offsetLeft + column.clientWidth / 2
        };
      })
      .filter(Boolean);

    if (centers.length === 0) return;

    const nearestIndex = centers.reduce((closestIndex, item, index, items) => {
      const currentDistance = Math.abs(item.center - visibleCenter);
      const closestDistance = Math.abs(
        items[closestIndex].center - visibleCenter
      );

      return currentDistance < closestDistance ? index : closestIndex;
    }, 0);

    const targetIndex = Math.min(
      centers.length - 1,
      Math.max(0, nearestIndex + direction)
    );

    scrollToProject(centers[targetIndex].uid);
  };

  const handleBoardWheel = event => {
    const container = boardScrollRef.current;
    if (!container) return;

    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
    if (!hasHorizontalOverflow) return;

    event.preventDefault();
    container.scrollBy({
      left: event.deltaY,
      behavior: 'auto'
    });
  };

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;

    const activePlacement = parseRepoPlacementId(active.id);
    const overId = over.id;
    const overPlacement = parseRepoPlacementId(overId);
    const fromContainer =
      activePlacement?.projectUid || findContainer(active.id);
    const toContainer = projects[overId]
      ? overId
      : overPlacement?.projectUid || findContainer(overId);
    const activeRepoId = activePlacement?.repoId || active.id;

    if (!fromContainer || !toContainer || fromContainer === toContainer) return;

    const activeRepo = projects[fromContainer]?.repos.find(
      repo => repo.id === activeRepoId
    );

    if (!activeRepo) return;

    const draggedRepos =
      selectedRepoIds.includes(active.id) && selectedRepos.length > 1
        ? selectedRepos
        : [
            {
              ...activeRepo,
              projectUid: fromContainer,
              projectName: projects[fromContainer]?.name,
              placementId: active.id
            }
          ];

    setPendingRepoAction({
      repos: draggedRepos,
      repoName: activeRepo.name,
      toProjectUid: toContainer,
      toProjectName: projects[toContainer]?.name
    });
  };

  const handleAddProject = async () => {
    try {
      const trimmed = newProjectName.trim();
      const projectExists = Object.values(projects || {}).some(
        project => project.name.toLowerCase() === trimmed.toLowerCase()
      );

      if (trimmed && !projectExists) {
        const response = await sendRequest('/project-management/set', 'POST', {
          project: trimmed
        });

        setProjects(prev => ({
          ...prev,
          [response.project]: {
            uid: response.project,
            name: trimmed,
            repos: []
          }
        }));
        toast.success('Project added successfully.');
        setNewProjectName('');
      } else {
        toast.error('Project name cannot be empty or already exists.');
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong.');
    }
  };

  const updateProjectSuggestionDraftGroup = (groupId, updater) => {
    setProjectSuggestionDraftGroups(prevGroups =>
      prevGroups.map(group => (group.id === groupId ? updater(group) : group))
    );
  };

  const handleProjectSuggestionNameChange = (groupId, projectName) => {
    updateProjectSuggestionDraftGroup(groupId, group => ({
      ...group,
      projectName
    }));
  };

  const handleProjectSuggestionStatusChange = (groupId, status) => {
    updateProjectSuggestionDraftGroup(groupId, group => ({
      ...group,
      status
    }));
  };

  const handleApplyProjectSuggestionGroup = async groupId => {
    if (!projectSuggestion?.id) return;

    const group = projectSuggestionDraftGroups.find(
      item => item.id === groupId
    );
    const projectName = String(group?.projectName || '').trim();

    if (!group || group.repos.length === 0) {
      toast.error('Select at least one repository before applying.');
      return;
    }

    if (!projectName || projectName.toLowerCase() === 'default') {
      toast.error('Project name cannot be empty or Default.');
      return;
    }

    setProjectSuggestionGroupAction(groupId);
    try {
      const response = await sendRequest(
        `${PROJECT_SUGGESTIONS_URL}/${projectSuggestion.id}/apply-group`,
        'POST',
        {
          project_name: projectName,
          repos: group.repos.map(repo => repo.repo_uid)
        }
      );

      if (response.success) {
        toast.success(`${projectName} project created from suggestion.`);
        await refreshProjectManagement();
      }
    } catch (error) {
      toast.error(error.message || 'Project suggestion could not be applied.');
    } finally {
      setProjectSuggestionGroupAction(null);
    }
  };

  const handleRemoveProjectSuggestionRepo = (groupId, repoUid) => {
    updateProjectSuggestionDraftGroup(groupId, group => {
      const repoToRemove = group.repos.find(repo => repo.repo_uid === repoUid);
      if (!repoToRemove) return group;

      return {
        ...group,
        repos: group.repos.filter(repo => repo.repo_uid !== repoUid),
        removedRepos: [...group.removedRepos, repoToRemove]
      };
    });
  };

  const handleToggleProjectSuggestionRepo = (groupId, repoUid) => {
    const group = projectSuggestionDraftGroups.find(
      item => item.id === groupId
    );
    if (!group) return;

    if (group.repos.some(repo => repo.repo_uid === repoUid)) {
      handleRemoveProjectSuggestionRepo(groupId, repoUid);
      return;
    }

    handleRestoreProjectSuggestionRepo(groupId, repoUid);
  };

  const handleRestoreProjectSuggestionRepo = (groupId, repoUid) => {
    updateProjectSuggestionDraftGroup(groupId, group => {
      const repoToRestore = group.removedRepos.find(
        repo => repo.repo_uid === repoUid
      );
      if (!repoToRestore) return group;

      return {
        ...group,
        repos: [...group.repos, repoToRestore],
        removedRepos: group.removedRepos.filter(
          repo => repo.repo_uid !== repoUid
        )
      };
    });
  };

  const handleProjectSuggestionAction = async action => {
    if (!projectSuggestion?.id) return;

    setProjectSuggestionAction(action);
    try {
      const { approvedGroups } = getProjectSuggestionDraftStats(
        projectSuggestionDraftGroups
      );
      const payload =
        action === 'apply'
          ? {
              groups: approvedGroups.map(group => ({
                project_name: group.projectName,
                repos: group.repos.map(repo => repo.repo_uid)
              }))
            }
          : undefined;
      const response = await sendRequest(
        `${PROJECT_SUGGESTIONS_URL}/${projectSuggestion.id}/${action}`,
        'POST',
        payload
      );

      if (response.success) {
        toast.success(
          action === 'apply'
            ? 'Project suggestions applied.'
            : 'Project suggestion dismissed.'
        );
        await refreshProjectManagement();
      }
    } catch (error) {
      toast.error(error.message || 'Project suggestion update failed.');
    } finally {
      setProjectSuggestionAction(null);
    }
  };

  const handleDeleteProject = async projectUid => {
    try {
      const confirmed = window.confirm(
        `Delete project "${projects[projectUid].name}"?`
      );
      if (!confirmed) return;

      await sendRequest('/project-management/delete', 'DELETE', { projectUid });

      setProjects(prev => {
        const newProjects = { ...prev };
        delete newProjects[projectUid];
        return newProjects;
      });
    } catch (err) {
      toast.error(err.message || 'Something went wrong.');
    }
  };

  const handleRenameProject = async (oldName, newName) => {
    const projectUid = oldName;
    const oldProjectName = { ...projects };

    const nameExists = Object.entries(projects)
      .filter(([uid]) => uid !== projectUid)
      .some(
        ([, project]) => project.name.toLowerCase() === newName.toLowerCase()
      );

    if (nameExists) {
      toast.error('Project name already exists.');
      return;
    }

    try {
      setProjects(prev => ({
        ...prev,
        [projectUid]: {
          ...prev[projectUid],
          name: newName
        }
      }));

      await sendRequest('/project-management/rename', 'PATCH', {
        newName,
        projectUid
      });
    } catch (err) {
      setProjects(oldProjectName);
      toast.error(err.message || 'Something went wrong.');
    }
  };

  const handleRenameRepo = async (projectName, repoId, newName) => {
    try {
      console.log('Rename repo', projectName, repoId, newName);
      return; // Backend connection pending
    } catch (err) {
      toast.error(err.message || 'Something went wrong.');
    }
  };

  const toggleRepoSelection = repoId => {
    setSelectedRepoIds(prev =>
      prev.includes(repoId)
        ? prev.filter(selectedId => selectedId !== repoId)
        : [...prev, repoId]
    );
  };

  const handleToggleSelectAll = projectUid => {
    const projectRepos = projects[projectUid]?.repos || [];
    const projectRepoIds = projectRepos.map(repo =>
      getRepoPlacementId(projectUid, repo.id)
    );
    const allSelected = projectRepoIds.every(repoId =>
      selectedRepoIds.includes(repoId)
    );

    setSelectedRepoIds(prev => {
      if (allSelected) {
        return prev.filter(repoId => !projectRepoIds.includes(repoId));
      }

      return [...new Set([...prev, ...projectRepoIds])];
    });
  };

  const handleRepoActionSelection = async operation => {
    if (!pendingRepoAction) return;

    const { repos, repoName, toProjectUid, toProjectName } = pendingRepoAction;
    const reposToUpdate = getReposForProjectAction(
      repos,
      toProjectUid,
      operation
    );

    if (reposToUpdate.length === 0) {
      toast.info(
        operation === 'copy'
          ? `${repoName} is already in ${toProjectName}.`
          : 'Selected repositories are already assigned to this project.'
      );
      setPendingRepoAction(null);
      return;
    }

    const previousProjects = { ...projects };

    setIsRepoActionSubmitting(true);
    try {
      setProjects(prev =>
        reposToUpdate.reduce(
          (nextProjects, repo) =>
            applyRepoProjectAction(nextProjects, {
              repoId: repo.id,
              fromProjectUid: repo.projectUid,
              toProjectUid,
              operation
            }),
          prev
        )
      );

      await Promise.all(
        reposToUpdate.map(repo =>
          sendRequest(
            '/project-management/repo/rename',
            'PATCH',
            buildRepoProjectPayload({
              repoId: repo.id,
              fromProjectUid: repo.projectUid,
              toProjectUid,
              operation
            })
          )
        )
      );

      setSelectedRepoIds(prev =>
        prev.filter(
          selectedPlacementId =>
            !reposToUpdate.some(
              repo => repo.placementId === selectedPlacementId
            )
        )
      );
      setPendingRepoAction(null);
      toast.success(
        `${reposToUpdate.length} repositor${
          reposToUpdate.length === 1 ? 'y' : 'ies'
        } ${operation === 'copy' ? 'copied' : 'moved'} to ${toProjectName}.`
      );
    } catch (err) {
      setProjects(previousProjects);
      toast.error(err.message || 'Repository action failed.');
    } finally {
      setIsRepoActionSubmitting(false);
    }
  };

  const handleBulkProjectAction = async operation => {
    if (!bulkTargetProjectUid || selectedRepos.length === 0) return;

    const reposToUpdate = getReposForProjectAction(
      selectedRepos,
      bulkTargetProjectUid,
      operation
    );

    if (reposToUpdate.length === 0) {
      toast.info(
        operation === 'copy'
          ? 'Selected repositories are already available in this project.'
          : 'Selected repositories are already assigned to this project.'
      );
      return;
    }

    const previousProjects = { ...projects };

    setBulkActionInFlight(operation);
    try {
      setProjects(prev =>
        reposToUpdate.reduce(
          (nextProjects, repo) =>
            applyRepoProjectAction(nextProjects, {
              repoId: repo.id,
              fromProjectUid: repo.projectUid,
              toProjectUid: bulkTargetProjectUid,
              operation
            }),
          prev
        )
      );

      await Promise.all(
        reposToUpdate.map(repo =>
          sendRequest(
            '/project-management/repo/rename',
            'PATCH',
            buildRepoProjectPayload({
              repoId: repo.id,
              fromProjectUid: repo.projectUid,
              toProjectUid: bulkTargetProjectUid,
              operation
            })
          )
        )
      );

      setSelectedRepoIds([]);
      setBulkTargetProjectUid('');
      toast.success(
        `${reposToUpdate.length} repositories ${
          operation === 'copy' ? 'copied' : 'moved'
        } successfully.`
      );
    } catch (err) {
      setProjects(previousProjects);
      toast.error(
        err.message || `Bulk ${operation === 'copy' ? 'copy' : 'move'} failed.`
      );
    } finally {
      setBulkActionInFlight(null);
    }
  };

  const hasProjects = orderedProjects.length > 0;
  const shouldShowProjectNotification =
    !isLoadingProjects && hasProjects && !hasCustomProjects;
  const projectSuggestionDraftStats = useMemo(
    () => getProjectSuggestionDraftStats(projectSuggestionDraftGroups),
    [projectSuggestionDraftGroups]
  );

  return (
    <div
      className={`rounded-sm border p-6 shadow-default md:p-6 xl:p-9 ${
        isDarkMode ? 'border-strokedark bg-boxdark' : 'border-stroke bg-white'
      }`}
    >
      {!isLoadingProjectSuggestion && projectSuggestion && (
        <section
          className={`mb-6 rounded-[22px] border p-5 ${
            isDarkMode
              ? 'border-amber-700/50 bg-amber-900/10'
              : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p
                className={`text-sm font-semibold ${
                  isDarkMode ? 'text-amber-200' : 'text-amber-800'
                }`}
              >
                {getSuggestionSummary(projectSuggestion)}
              </p>
              <p
                className={`mt-1 text-sm ${
                  isDarkMode ? 'text-amber-100/80' : 'text-amber-700'
                }`}
              >
                Review, edit, include, or reject suggested projects before
                creating them.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {isProjectSuggestionOpen && (
                <>
                  <button
                    type="button"
                    disabled={
                      !!projectSuggestionAction ||
                      projectSuggestionDraftStats.approvedRepoCount === 0 ||
                      projectSuggestionDraftStats.invalidApprovedProjectNameCount >
                        0
                    }
                    onClick={() => handleProjectSuggestionAction('apply')}
                    className="inline-flex items-center justify-center rounded-2xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {projectSuggestionAction === 'apply'
                      ? 'Applying...'
                      : `Apply (${projectSuggestionDraftStats.approvedProjectCount} project${projectSuggestionDraftStats.approvedProjectCount === 1 ? '' : 's'}, ${projectSuggestionDraftStats.approvedRepoCount} repositor${projectSuggestionDraftStats.approvedRepoCount === 1 ? 'y' : 'ies'})`}
                  </button>
                  <button
                    type="button"
                    disabled={!!projectSuggestionAction}
                    onClick={() => handleProjectSuggestionAction('dismiss')}
                    className={`inline-flex items-center justify-center rounded-2xl border px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                      isDarkMode
                        ? 'border-red-500/40 text-red-200 hover:bg-red-500/10'
                        : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    {projectSuggestionAction === 'dismiss'
                      ? 'Dismissing...'
                      : 'Dismiss'}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setIsProjectSuggestionOpen(value => !value)}
                className={`inline-flex items-center justify-center rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                  isDarkMode
                    ? 'bg-amber-300 text-slate-950 hover:bg-amber-200'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {isProjectSuggestionOpen
                  ? 'Hide suggestions'
                  : 'Review suggestions'}
              </button>
            </div>
          </div>

          {isProjectSuggestionOpen && (
            <div className="mt-5 grid gap-4">
              <div
                className={`rounded-2xl border px-4 py-3 text-sm ${
                  isDarkMode
                    ? 'border-amber-700/40 bg-slate-950/30 text-slate-300'
                    : 'border-amber-200 bg-white text-slate-600'
                }`}
              >
                {projectSuggestionDraftStats.approvedProjectCount} project
                {projectSuggestionDraftStats.approvedProjectCount === 1
                  ? ''
                  : 's'}{' '}
                and {projectSuggestionDraftStats.approvedRepoCount} repositor
                {projectSuggestionDraftStats.approvedRepoCount === 1
                  ? 'y'
                  : 'ies'}{' '}
                are included. {projectSuggestionDraftStats.rejectedProjectCount}{' '}
                rejected, {projectSuggestionDraftStats.removedRepoCount} repo
                {projectSuggestionDraftStats.removedRepoCount === 1
                  ? ''
                  : 's'}{' '}
                removed from suggestions.
                {projectSuggestionDraftStats.invalidApprovedProjectNameCount > 0
                  ? ' Rename included suggestions with empty or Default names before applying.'
                  : ''}
              </div>

              {projectSuggestionDraftGroups.map(group => (
                <div
                  key={group.id}
                  className={`rounded-[18px] border p-4 ${
                    isDarkMode
                      ? 'border-amber-700/40 bg-slate-950/30'
                      : 'border-amber-200 bg-white'
                  }`}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <label
                        className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${
                          isDarkMode ? 'text-slate-400' : 'text-slate-500'
                        }`}
                      >
                        Project name
                      </label>
                      <input
                        value={group.projectName}
                        onChange={event =>
                          handleProjectSuggestionNameChange(
                            group.id,
                            event.target.value
                          )
                        }
                        disabled={group.status === 'rejected'}
                        className={`h-11 w-full max-w-xl rounded-xl border px-3 text-sm font-semibold transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-60 ${
                          isDarkMode
                            ? 'border-slate-700 bg-boxdark text-white'
                            : 'border-slate-200 bg-slate-50 text-slate-900'
                        }`}
                      />
                      <p
                        className={`mt-2 text-sm ${
                          isDarkMode ? 'text-slate-400' : 'text-slate-500'
                        }`}
                      >
                        {group.repos.length} included,{' '}
                        {group.removedRepos.length} removed. Original:{' '}
                        {group.originalProjectName}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={
                          group.status === 'rejected' ||
                          projectSuggestionGroupAction === group.id ||
                          group.repos.length === 0 ||
                          !String(group.projectName || '').trim() ||
                          String(group.projectName || '')
                            .trim()
                            .toLowerCase() === 'default'
                        }
                        onClick={() =>
                          handleApplyProjectSuggestionGroup(group.id)
                        }
                        className="inline-flex items-center justify-center rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {projectSuggestionGroupAction === group.id
                          ? 'Applying...'
                          : 'Apply'}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleProjectSuggestionStatusChange(
                            group.id,
                            group.status === 'rejected'
                              ? 'approved'
                              : 'rejected'
                          )
                        }
                        className={`inline-flex items-center justify-center rounded-2xl border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                          group.status === 'rejected'
                            ? isDarkMode
                              ? 'border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10'
                              : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            : isDarkMode
                              ? 'border-red-500/40 text-red-200 hover:bg-red-500/10'
                              : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        {group.status === 'rejected' ? 'Restore' : 'Reject'}
                      </button>
                    </div>
                  </div>

                  {group.status !== 'rejected' && (
                    <div
                      className={`mt-4 max-h-64 overflow-auto rounded-2xl border ${
                        isDarkMode
                          ? 'border-slate-700 bg-boxdark'
                          : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <table className="min-w-full text-left text-sm">
                        <thead
                          className={`sticky top-0 z-10 ${
                            isDarkMode
                              ? 'bg-slate-900 text-slate-300'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          <tr>
                            <th className="px-4 py-3 font-semibold">
                              Repository
                            </th>
                            <th className="px-4 py-3 font-semibold">URL</th>
                            <th className="px-4 py-3 text-right font-semibold">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...group.repos, ...group.removedRepos]
                            .sort(
                              (left, right) =>
                                (left.originalIndex || 0) -
                                (right.originalIndex || 0)
                            )
                            .map(repo => {
                              const isRemoved = group.removedRepos.some(
                                removedRepo =>
                                  removedRepo.repo_uid === repo.repo_uid
                              );

                              return (
                                <tr
                                  key={`${group.id}-${repo.repo_uid}`}
                                  title={repo.reason || 'No reason provided.'}
                                  className={`border-t ${
                                    isDarkMode
                                      ? 'border-slate-700 text-slate-200'
                                      : 'border-slate-200 text-slate-700'
                                  }`}
                                >
                                  <td
                                    className={`max-w-[220px] px-4 py-3 ${
                                      isRemoved ? 'opacity-60' : ''
                                    }`}
                                  >
                                    <div className="truncate font-semibold">
                                      {repo.display_name ||
                                        repo.repo_name ||
                                        repo.full_name ||
                                        repo.repo_uid}
                                    </div>
                                  </td>
                                  <td
                                    className={`max-w-[280px] px-4 py-3 ${
                                      isRemoved ? 'opacity-60' : ''
                                    }`}
                                  >
                                    <span className="block truncate">
                                      {repo.clone_url || repo.full_name || '-'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleToggleProjectSuggestionRepo(
                                          group.id,
                                          repo.repo_uid
                                        )
                                      }
                                      className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
                                        isRemoved
                                          ? isDarkMode
                                            ? 'border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10'
                                            : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                          : isDarkMode
                                            ? 'border-red-500/40 text-red-200 hover:bg-red-500/10'
                                            : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                                      }`}
                                    >
                                      {isRemoved ? 'Restore' : 'Remove'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          {group.repos.length === 0 &&
                            group.removedRepos.length === 0 && (
                              <tr>
                                <td
                                  colSpan="3"
                                  className={`px-4 py-5 text-center text-sm ${
                                    isDarkMode
                                      ? 'text-slate-400'
                                      : 'text-slate-500'
                                  }`}
                                >
                                  No repositories included in this project
                                  suggestion.
                                </td>
                              </tr>
                            )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}

              {projectSuggestion.remaining_default_count > 0 && (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm ${
                    isDarkMode
                      ? 'border-slate-700 bg-slate-900/40 text-slate-300'
                      : 'border-slate-200 bg-slate-50 text-slate-600'
                  }`}
                >
                  {projectSuggestion.remaining_default_count} repositor
                  {projectSuggestion.remaining_default_count === 1
                    ? 'y'
                    : 'ies'}{' '}
                  will stay in Default because GitMe did not classify them
                  confidently.
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex min-w-[280px] flex-1 flex-wrap items-center gap-3 lg:min-w-[360px] lg:flex-nowrap">
          <input
            type="text"
            value={newProjectName}
            placeholder="New project name"
            onChange={e => setNewProjectName(e.target.value)}
            className={`h-11 min-w-0 flex-1 rounded-xl border px-4 text-sm shadow-sm transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDarkMode
                ? 'border-strokedark bg-meta-4 text-white'
                : 'border-neutral-300 bg-white'
            }`}
          />
          <button
            onClick={handleAddProject}
            className="flex h-11 items-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow transition-all hover:bg-blue-700 active:scale-95"
          >
            <AddIcon fontSize="small" />
            Add Project
          </button>
        </div>
      </div>

      {shouldShowProjectNotification && (
        <div className="mb-6 rounded-sm border border-amber-200 bg-amber-50 px-4 py-4 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            No custom projects yet
          </p>
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-100/80">
            {defaultProject
              ? `"${defaultProject.name}" was created automatically. Add your first project to organize repositories beyond it.`
              : 'Create your first project to start organizing repositories.'}
          </p>
        </div>
      )}

      <div className="pb-10">
        {selectedRepos.length > 0 && (
          <div
            className={`mb-5 flex flex-col gap-4 rounded-[24px] border px-5 py-4 lg:flex-row lg:items-center lg:justify-between ${
              isDarkMode
                ? 'border-blue-800/70 bg-[linear-gradient(135deg,rgba(30,41,59,0.95),rgba(15,23,42,0.9))]'
                : 'border-blue-200 bg-[linear-gradient(135deg,rgba(239,246,255,0.95),rgba(255,255,255,0.98))]'
            }`}
          >
            <div className="space-y-1">
              <p
                className={`text-sm font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                {selectedRepos.length} repositories selected
              </p>
              <p
                className={`text-sm ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}
              >
                Choose a destination project, then use Move to relocate the
                selected repositories or Copy to keep them in their current
                projects and add them to another one too.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={bulkTargetProjectUid}
                onChange={e => setBulkTargetProjectUid(e.target.value)}
                className={`h-11 min-w-[220px] rounded-xl border px-4 text-sm shadow-sm outline-none focus:border-blue-400 focus:ring-2 ${
                  isDarkMode
                    ? 'border-strokedark bg-meta-4 text-slate-100 focus:ring-blue-900/40'
                    : 'border-slate-200 bg-white text-slate-700 focus:ring-blue-100'
                }`}
              >
                <option value="">Move or copy selected repos to...</option>
                {orderedProjects.map(project => (
                  <option key={project.uid} value={project.uid}>
                    {project.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => handleBulkProjectAction('move')}
                disabled={!bulkTargetProjectUid || !!bulkActionInFlight}
                className="h-11 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {bulkActionInFlight === 'move' ? (
                  <Loading type="inline" />
                ) : (
                  'Move selected'
                )}
              </button>
              <button
                type="button"
                onClick={() => handleBulkProjectAction('copy')}
                disabled={!bulkTargetProjectUid || !!bulkActionInFlight}
                className="h-11 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {bulkActionInFlight === 'copy' ? (
                  <Loading type="inline" />
                ) : (
                  'Copy selected'
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedRepoIds([]);
                  setBulkTargetProjectUid('');
                }}
                className={`h-11 rounded-xl border px-4 text-sm font-semibold transition ${
                  isDarkMode
                    ? 'border-strokedark bg-boxdark text-slate-200 hover:border-slate-600 hover:text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                }`}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4
                className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                Projects
              </h4>
              <p
                className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
              >
                Scroll sideways or drag a repository toward the screen edge to
                reach projects further right.
              </p>
            </div>

            {hasProjects && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => scrollToAdjacentProject(-1)}
                  disabled={!canScrollLeft}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    isDarkMode
                      ? 'border-strokedark bg-boxdark text-slate-200 hover:border-slate-600 hover:text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                  }`}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => scrollToAdjacentProject(1)}
                  disabled={!canScrollRight}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    isDarkMode
                      ? 'border-strokedark bg-boxdark text-slate-200 hover:border-slate-600 hover:text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {hasProjects && (
            <div className="mb-4 flex gap-2 overflow-x-auto pb-2">
              {orderedProjects.map(({ uid, name, repos }) => (
                <button
                  key={uid}
                  type="button"
                  onClick={() => scrollToProject(uid)}
                  className={`shrink-0 rounded-full border px-3 py-2 text-sm font-medium transition ${
                    isDarkMode
                      ? 'border-strokedark bg-boxdark text-slate-200 hover:border-blue-700 hover:bg-blue-950/40 hover:text-blue-300'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                  }`}
                >
                  {name} ({repos.length})
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            {hasProjects && canScrollLeft && (
              <div
                className={`pointer-events-none absolute inset-y-0 left-0 z-10 w-10 ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-boxdark via-boxdark/80 to-transparent'
                    : 'bg-gradient-to-r from-white via-white/80 to-transparent'
                }`}
              />
            )}
            {hasProjects && canScrollRight && (
              <div
                className={`pointer-events-none absolute inset-y-0 right-0 z-10 w-10 ${
                  isDarkMode
                    ? 'bg-gradient-to-l from-boxdark via-boxdark/80 to-transparent'
                    : 'bg-gradient-to-l from-white via-white/80 to-transparent'
                }`}
              />
            )}

            {isLoadingProjects ? (
              <Loading />
            ) : hasProjects ? (
              <div
                ref={boardScrollRef}
                onWheel={handleBoardWheel}
                className={`no-scrollbar w-full overflow-x-auto scroll-smooth rounded-[28px] border p-3 ${
                  isDarkMode
                    ? 'border-strokedark bg-boxdark-2'
                    : 'border-slate-200 bg-slate-50/80'
                }`}
              >
                <div className="flex min-w-max snap-x snap-mandatory gap-4">
                  {orderedProjects.map(({ uid, name: title, repos }) => (
                    <div
                      key={uid}
                      ref={node => {
                        if (node) {
                          columnRefs.current[uid] = node;
                        } else {
                          delete columnRefs.current[uid];
                        }
                      }}
                      className="shrink-0 snap-center"
                    >
                      <ProjectColumn
                        title={title}
                        repos={repos}
                        projectUid={uid}
                        onDelete={handleDeleteProject}
                        onRename={handleRenameProject}
                        onRenameRepo={handleRenameRepo}
                        selectedRepoIds={selectedRepoIds}
                        onToggleRepoSelection={toggleRepoSelection}
                        onToggleSelectAll={handleToggleSelectAll}
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState
                title="No projects yet"
                description="You have not created any projects yet. Create your first project to start organizing repositories."
              />
            )}
          </div>

          <DragOverlay>
            {activeDraggedRepos.length > 0 &&
              (() => {
                const [firstRepo] = activeDraggedRepos;
                const isGroupDrag = activeDraggedRepos.length > 1;

                return (
                  <div className="relative w-[260px]">
                    {isGroupDrag && (
                      <>
                        <div
                          className={`absolute left-3 top-3 h-full w-full rounded-2xl border border-dashed ${
                            isDarkMode
                              ? 'border-slate-600 bg-slate-800/70'
                              : 'border-slate-300 bg-slate-100/90'
                          }`}
                        />
                        <div
                          className={`absolute left-1.5 top-1.5 h-full w-full rounded-2xl border border-dashed ${
                            isDarkMode
                              ? 'border-slate-500 bg-slate-900/85'
                              : 'border-slate-300 bg-slate-50/95'
                          }`}
                        />
                      </>
                    )}

                    <div
                      className={`relative flex items-center gap-2 rounded-2xl border border-dashed border-sky-500 px-3 py-3 text-sm font-medium shadow-xl ${
                        isDarkMode ? 'bg-boxdark text-slate-100' : 'bg-white'
                      }`}
                    >
                      <DragIndicatorIcon className="color-sky-500" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate">{firstRepo.name}</p>
                        {isGroupDrag && (
                          <p
                            className={`text-xs ${
                              isDarkMode ? 'text-slate-400' : 'text-slate-500'
                            }`}
                          >
                            +{activeDraggedRepos.length - 1} more selected
                          </p>
                        )}
                      </div>
                      {isGroupDrag && (
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            isDarkMode
                              ? 'bg-blue-500/15 text-blue-300'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {activeDraggedRepos.length}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
          </DragOverlay>
        </DndContext>
      </div>

      {pendingRepoAction && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px]"
            onClick={() => {
              if (!isRepoActionSubmitting) {
                setPendingRepoAction(null);
              }
            }}
          />
          <div
            className={`relative z-10 w-full max-w-md rounded-2xl border p-5 shadow-xl ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-slate-200 bg-white'
            }`}
          >
            <div className="space-y-3">
              <p
                className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                {pendingRepoAction.repos.length > 1
                  ? `${pendingRepoAction.repos.length} selected repositories`
                  : `Move or copy ${pendingRepoAction.repoName}?`}
              </p>
              <p
                className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}
              >
                Target project:{' '}
                <span className="font-semibold">
                  {pendingRepoAction.toProjectName}
                </span>
                . Move removes the dragged selection from its current projects.
                Copy keeps the existing placements and adds the destination too.
              </p>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setPendingRepoAction(null)}
                disabled={isRepoActionSubmitting}
                className={`h-11 rounded-xl border px-4 text-sm font-semibold transition ${
                  isDarkMode
                    ? 'border-strokedark bg-boxdark-2 text-slate-200 hover:border-slate-600 hover:text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleRepoActionSelection('copy')}
                disabled={isRepoActionSubmitting}
                className="h-11 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isRepoActionSubmitting ? <Loading type="inline" /> : 'Copy'}
              </button>
              <button
                type="button"
                onClick={() => handleRepoActionSelection('move')}
                disabled={isRepoActionSubmitting}
                className="h-11 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isRepoActionSubmitting ? <Loading type="inline" /> : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`flex w-full rounded-2xl border px-4 py-4 shadow-sm md:p-4 ${
          isDarkMode
            ? 'border-amber-500/30 bg-amber-500/10'
            : 'border-amber-200 bg-amber-50'
        }`}
      >
        <div className="w-full">
          <p
            className={`leading-relaxed ${
              isDarkMode ? 'text-amber-200' : 'text-amber-700'
            }`}
          >
            Tip: Double-click a repository name to rename it. Drag a repository
            toward another project to choose whether to move or copy it, and use
            the bulk action bar to apply the same idea to multiple repositories
            at once.
          </p>
        </div>
      </div>
    </div>
  );
};

RepoItem.propTypes = {
  id: PropTypes.string.isRequired,
  repoId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  dragOverlay: PropTypes.bool,
  onRename: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool.isRequired
};

ProjectColumn.propTypes = {
  title: PropTypes.string.isRequired,
  projectUid: PropTypes.string.isRequired,
  repos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  onDelete: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onRenameRepo: PropTypes.func.isRequired,
  selectedRepoIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleRepoSelection: PropTypes.func.isRequired,
  onToggleSelectAll: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool.isRequired
};

export default ProjectManagement;
